import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';

const ContactForm = ({ onClose }) => {
  const { isDarkMode } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const bgClass = isDarkMode ? 'bg-[#141414]' : 'bg-white';
  const textClass = isDarkMode ? 'text-[#F2F2F2]' : 'text-[#1A1A1A]';
  const subtextClass = isDarkMode ? 'text-[#999999]' : 'text-[#555555]';
  const borderClass = isDarkMode ? 'border-white/10' : 'border-[#E5E5E5]';
  const inputBgClass = isDarkMode ? 'bg-[#0A0A0A]' : 'bg-[#FAFAFA]';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error('Failed to submit form');

      setStatus({
        type: 'success',
        message: "Thanks for reaching out! We'll get back to you soon.",
      });
      setFormData({ name: '', email: '', company: '', message: '' });
      
      // Close modal after success if onClose is provided
      if (onClose) {
        setTimeout(() => onClose(), 2000);
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or email us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`${bgClass} rounded-none md:rounded-2xl p-6 pt-16 md:pt-8 md:p-8 shadow-2xl w-full min-h-screen md:min-h-0 md:max-w-lg mx-auto`}>
      <div className="mb-6">
        <h2 className={`font-display text-2xl md:text-3xl ${textClass} mb-2`}>
          Let's Talk
        </h2>
        <p className={`${subtextClass} text-sm md:text-base`}>
          Fill out the form below and we'll get back to you shortly.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="name" className={`block text-sm font-medium ${subtextClass} mb-1.5`}>
            Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className={`w-full px-4 py-3 rounded-xl border ${borderClass} ${inputBgClass} ${textClass} focus:outline-none focus:ring-2 focus:ring-[#F6CD17]/30 focus:border-[#F6CD17] transition-all text-base`}
            placeholder="Your name"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className={`block text-sm font-medium ${subtextClass} mb-1.5`}>
            Email *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className={`w-full px-4 py-3 rounded-xl border ${borderClass} ${inputBgClass} ${textClass} focus:outline-none focus:ring-2 focus:ring-[#F6CD17]/30 focus:border-[#F6CD17] transition-all text-base`}
            placeholder="you@company.com"
          />
        </div>

        {/* Company (Optional) */}
        <div>
          <label htmlFor="company" className={`block text-sm font-medium ${subtextClass} mb-1.5`}>
            Company <span className="text-[#999999]">(optional)</span>
          </label>
          <input
            type="text"
            id="company"
            name="company"
            value={formData.company}
            onChange={handleChange}
            className={`w-full px-4 py-3 rounded-xl border ${borderClass} ${inputBgClass} ${textClass} focus:outline-none focus:ring-2 focus:ring-[#F6CD17]/30 focus:border-[#F6CD17] transition-all text-base`}
            placeholder="Your company"
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className={`block text-sm font-medium ${subtextClass} mb-1.5`}>
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            rows={3}
            className={`w-full px-4 py-3 rounded-xl border ${borderClass} ${inputBgClass} ${textClass} focus:outline-none focus:ring-2 focus:ring-[#F6CD17]/30 focus:border-[#F6CD17] transition-all resize-none text-base`}
            placeholder="Tell us about your project..."
          />
        </div>

        {/* Status Message */}
        {status.message && (
          <div
            className={`p-3 rounded-xl text-sm ${
              status.type === 'success'
                ? 'bg-green-500/10 text-green-500 border border-green-500/20'
                : 'bg-red-500/10 text-red-500 border border-red-500/20'
            }`}
          >
            {status.message}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full py-3.5 px-6 rounded-xl font-medium transition-all duration-300 ${
            isSubmitting
              ? 'bg-[#F6CD17]/50 cursor-not-allowed text-black/50'
              : 'bg-[#F6CD17] hover:bg-[#e0b915] hover:shadow-lg active:scale-[0.98]'
          } text-black flex items-center justify-center gap-2`}
        >
          {isSubmitting ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Sending...
            </>
          ) : (
            <>
              Send Message
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" />
              </svg>
            </>
          )}
        </button>

        {/* Alternative Contact */}
        <p className={`text-center text-sm ${subtextClass} pb-4 md:pb-0`}>
          Or email us at{' '}
          <a
            href="mailto:contact@weareflinch.com"
            className="text-[#F6CD17] hover:underline"
          >
            contact@weareflinch.com
          </a>
        </p>
      </form>
    </div>
  );
};

export default ContactForm;
